import { ColumnType } from '@shared/enums/ColumnType';
import { ConnectorType } from '@shared/enums/ConnectorType';
import { ContactActionByType } from '@shared/enums/ContactActionByType';
import { ContactActionType } from '@shared/enums/ContactActionType';
import { ContactType } from '@shared/enums/ContactType';
import { CustomContactFieldType } from '@shared/enums/CustomContactFieldType';
import { ExcelExistingEmailAddressType } from '@shared/enums/ExcelExistingEmailAddressType';
import { FilterLogicOperators } from '@shared/enums/FilterLogicOperators';
import { FilterType } from '@shared/enums/FilterType';
import { FreeTextFormat } from '@shared/enums/FreeTextFormat';
import { IUnsubscribeSettingsOption } from '@shared/services/api/legacy/settings-service/setting-model';

export interface IContact {
    id: string;
    firstName: string;
    lastNamePrefix: string;
    lastName: string;
    title: string;
    type: ContactType;
    genderId: string;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    accountOwnerId: string;
    companyId: string;
    salutation: string;
    mailPermission: boolean;
    smsPermission: boolean;
    crmId: string;
    customerNumber: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    accessCode: string;
    contactCustomFields: IContactCustomField[];
    contactTags: IContactTag[];
    unsubscribes: IContactUnsubscribe[];
    logs: IContactLog[];
    canEdit: boolean;
    groupIds: string[];
}

export class Contact implements IContact {
    public id: string;
    public firstName: string;
    public lastNamePrefix: string;
    public lastName: string;
    public title: string;
    public type: ContactType;
    public genderId: string;
    public languageId: string;
    public email: string;
    public phone: string;
    public mobile: string;
    public companyName: string;
    public companyId: string;
    public accountOwnerId: string;
    public salutation: string;
    public mailPermission: boolean;
    public smsPermission: boolean;
    public crmId: string;
    public customerNumber: string;
    public addressLineOne: string;
    public addressLineTwo: string;
    public city: string;
    public postalCode: string;
    public country: string;
    public accessCode: string;
    public contactCustomFields: IContactCustomField[];
    public contactTags: IContactTag[];
    public unsubscribes: IContactUnsubscribe[];
    public logs: IContactLog[];
    public canEdit: boolean;
    public groupIds: string[];
}

export interface IContactUnsubscribe {
    unsubscribeDate: Date;
    unsubscribeOptionId: string;
}

export interface IContactLog {
    id: string;
    created: Date;
    unsubscribeOption: IContactUnsubscribeOption;
    user: IContactUnsubscribeUser;
    actionType: ContactActionType;
    actionByType: ContactActionByType;
    connectorType: ConnectorType;
}

export interface IContactUnsubscribeOption {
    id: string;
    name: string;
}

export interface IContactUnsubscribeUser {
    firstName: string;
    lastName: string;
    email: string;
}

export interface IAddContact {
    firstName: string;
    lastName: string;
    title: string;
    type: ContactType;
    genderId: string;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    accountOwnerId: string;
    salutation: string;
    mailPermission: boolean;
    smsPermission: boolean;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    contactCustomFields: IContactCustomField[];
    contactTags: IContactTag[];
    unsubscribes: IContactUnsubscribe[];
    logs: IContactLog[];
}

export class AddContact implements IAddContact {
    constructor(
        public firstName: string,
        public lastName: string,
        public title: string,
        public type: ContactType,
        public genderId: string,
        public languageId: string,
        public email: string,
        public phone: string,
        public mobile: string,
        public companyName: string,
        public salutation: string,
        public mailPermission: boolean,
        public smsPermission: boolean,
        public accountOwnerId: string,
        public addressLineOne: string,
        public addressLineTwo: string,
        public city: string,
        public postalCode: string,
        public country: string,
        public contactCustomFields: IContactCustomField[],
        public contactTags: IContactTag[],
        public unsubscribes: IContactUnsubscribe[],
        public logs: IContactLog[]
    ) { }
}

export interface IUpdateContact {
    id: string;
    firstName: string;
    lastName: string;
    title: string;
    type: ContactType;
    genderId: string;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    accountOwnerId: string;
    salutation: string;
    mailPermission: boolean;
    smsPermission: boolean;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    contactCustomFields: IContactCustomField[];
    contactTags: IContactTag[];
    unsubscribes: IContactUnsubscribe[];
    logs: IContactLog[];
}

export class UpdateContact implements IUpdateContact {
    constructor(
        public id: string,
        public firstName: string,
        public lastName: string,
        public title: string,
        public type: ContactType,
        public genderId: string,
        public languageId: string,
        public email: string,
        public phone: string,
        public mobile: string,
        public companyName: string,
        public accountOwnerId: string,
        public salutation: string,
        public mailPermission: boolean,
        public smsPermission: boolean,
        public addressLineOne: string,
        public addressLineTwo: string,
        public city: string,
        public postalCode: string,
        public country: string,
        public contactCustomFields: IContactCustomField[],
        public contactTags: IContactTag[],
        public unsubscribes: IContactUnsubscribe[],
        public logs: IContactLog[]
    ) { }
}

export interface IPrepareExcelContactsRequest {
    accountOwnerId: string;
    languageId: string;
    contactType: string;
    data: any[][];
    contactColumns: IContactColumn[];
    permissionSettings: IPermissionSetting[];
}

export class PrepareExcelContactsRequest implements IPrepareExcelContactsRequest {
    constructor(
        public accountOwnerId: string,
        public languageId: string,
        public contactType: string,
        public data: any[][],
        public contactColumns: IContactColumn[],
        public permissionSettings: IPermissionSetting[]
    ) { }
}

export interface IPrepareDatabaseContactsRequest {
    contacts: IImportData[];
    doublesEmailAddressExcel: IImportData[];
    excelExistingEmailAddressType: ExcelExistingEmailAddressType;
    canUpdateLanguageId: boolean;
    canUpdateAccountOwnerId: boolean;
    permissionSettings: IPermissionSetting[];
}

export class PrepareDatabaseContactsRequest implements IPrepareDatabaseContactsRequest {
    constructor(
        public contacts: IImportData[],
        public doublesEmailAddressExcel: IImportData[],
        public excelExistingEmailAddressType: ExcelExistingEmailAddressType,
        public canUpdateLanguageId: boolean,
        public canUpdateAccountOwnerId: boolean,
        public permissionSettings: IPermissionSetting[]
    ) { }
}

export interface IImportData {
    contact: IImportContact;
    errorMessage: string;
    row: number;
}

export interface IImportContact {
    firstName: string;
    lastName: string;
    genderId: string;
    jobTitle: string;
    type: ContactType;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    companyId: string;
    accountOwnerId: string;
    crmId: string;
    customerNumber: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    salutation: string;
    ticketHolderId: string;
    ticketHolderName: string;
    accessCode: string;
}

export interface IContactColumn {
    columnIndex: number;
    columnId: string;
    columnType: ColumnType;
}

export class ContactColumn implements IContactColumn {
    constructor(
        public columnIndex: number,
        public columnId: string,
        public columnType: ColumnType
    ) { }
}

export interface IPermissionSetting {
    id: string;
    name: string;
    defaultValue: boolean;
    canUpdate: boolean;
}

export class PermissionSetting implements IPermissionSetting {
    constructor(
        public id: string,
        public name: string,
        public defaultValue: boolean,
        public canUpdate: boolean
    ) { }
}

export interface IContactCustomField {
    id: string;
    index: number;
    customFieldType: CustomContactFieldType;
    freeTextFormat: FreeTextFormat;
    translations: IFieldTranslations[];
    availableCustomFieldOptions: ICustomContactFieldOption[];
    contactCustomFieldOptions: ICustomContactFieldOption[];
    contactCustomFieldUsers: ICustomContactFieldUser[];
    booleanValue: boolean;
    textValue: string;
    numberValue: number;
    dateTimeValue: Date;
}

export class ContactCustomField implements IContactCustomField {
    constructor(
        public id: string,
        public index: number,
        public customFieldType: CustomContactFieldType,
        public freeTextFormat: FreeTextFormat,
        public translations: IFieldTranslations[],
        public availableCustomFieldOptions: ICustomContactFieldOption[],
        public contactCustomFieldOptions: ICustomContactFieldOption[],
        public contactCustomFieldUsers: ICustomContactFieldUser[],
        public booleanValue: boolean,
        public textValue: string,
        public numberValue: number,
        public dateTimeValue: Date
    ) { }
}

export interface ICustomContactFieldOption {
    id: string;
    index: number;
    translations: IFieldTranslations[];
}

export class CustomContactFieldOption implements ICustomContactFieldOption {
    constructor(
        public id: string,
        public index: number,
        public translations: IFieldTranslations[],
    ) { }
}

export interface ICustomContactFieldUser {
    userId: string;
}

export class CustomContactFieldUser implements ICustomContactFieldUser {
    constructor(
        public userId: string
    ) { }
}

export interface IFieldTranslations {
    id: string;
    value: string;
    isDefault: boolean;
    languageId: string;
}

export interface IContactTag {
    id: string;
    translations: IFieldTranslations[];
}
export class ContactTag implements IContactTag {
    constructor(
        public id: string,
        public translations: IFieldTranslations[]
    ) { }
}

export interface ITag {
    value: string;
    display: string;
}

export class Tag implements ITag {
    constructor(
        public value: string,
        public display: string
    ) { }
}

export interface IFilterField {
    id: string;
    filterId: string;
    filterValue: string;
    filterValueTwo: string;
    filterValueItems: [];
    logicOperator: FilterLogicOperators;
    filterValueType: string;
    filterType: FilterType;
    customFieldId: string;
    unsubscribeOptionId: string;
}

export class FilterField implements IFilterField {
    constructor(
        public id: string,
        public filterValue: string,
        public filterId: string,
        public filterValueTwo: string,
        public filterValueItems: [],
        public logicOperator: FilterLogicOperators,
        public filterValueType: string,
        public filterType: FilterType,
        public customFieldId: string,
        public unsubscribeOptionId: string
    ) { }
}

export interface IExportContactSettings {
    fileName: string;
    canExportFirstName: boolean;
    canExportLastNamePrefix: boolean;
    canExportLastName: boolean;
    canExportEmail: boolean;
    canExportMobile: boolean;
    canExportPhone: boolean;
    canExportJobTitle: boolean;
    canExportCompanyName: boolean;
    canExportAccountOwner: boolean;
    canExportLanguageId: boolean;
    canExportContactType: boolean;
    canExportGenderId: boolean;
    canExportSalutation: boolean;
    canExportCrmId: boolean;
    canExportCustomerNumber: boolean;
    canExportSmsPermission: boolean;
    canExportAddressLineOne: boolean;
    canExportAddressLineTwo: boolean;
    canExportCity: boolean;
    canExportPostalCode: boolean;
    canExportCountry: boolean;
    canExportAccessCode: boolean;
    canExportIsActive: boolean;
    canExportCustomFields: string[];
    canExportUnsubscribeOptions: IUnsubscribeSettingsOption[];
    canExportTags: boolean;
}

export class ExportContactSettings implements IExportContactSettings {
    public fileName: string;
    public canExportFirstName: boolean;
    public canExportLastNamePrefix: boolean;
    public canExportLastName: boolean;
    public canExportEmail: boolean;
    public canExportMobile: boolean;
    public canExportPhone: boolean;
    public canExportJobTitle: boolean;
    public canExportCompanyName: boolean;
    public canExportAccountOwner: boolean;
    public canExportLanguageId: boolean;
    public canExportContactType: boolean;
    public canExportGenderId: boolean;
    public canExportSalutation: boolean;
    public canExportCrmId: boolean;
    public canExportCustomerNumber: boolean;
    public canExportSmsPermission: boolean;
    public canExportAddressLineOne: boolean;
    public canExportAddressLineTwo: boolean;
    public canExportCity: boolean;
    public canExportPostalCode: boolean;
    public canExportCountry: boolean;
    public canExportAccessCode: boolean;
    public canExportIsActive: boolean;
    public canExportCustomFields: string[];
    public canExportUnsubscribeOptions: IUnsubscribeSettingsOption[];
    public canExportTags: boolean;
}

